// filepath: apps/web/src/features/observability/ActiveAlertsPanel.tsx
//
// FR-UI-DASH-1 AC-3 + AC-3a -- Active Alerts panel.
//
// The endpoint (`GET /api/observability/alerts/active`) does NOT
// yet exist (FR-OBS-4 is not started). The SPA tolerates the
// 404/501 response by rendering an EMPTY array, and the panel
// renders the typed empty state with `data-state="awaiting_fr_obs_4"`
// so UAT and future tests see the gap is tracked.
//
// When FR-OBS-4 ships (active-alerts endpoint + scheduler), the
// panel will receive a populated `ActiveAlert[]` and render each
// row.
//
// INV-9: every string field is a React text node; no
// `dangerouslySetInnerHTML`.
// INV-1: tenant scoping is server-side; the alerts are pre-filtered
// by tenant_id.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import type { ActiveAlert } from "./types";

export interface ActiveAlertsPanelProps {
  alerts: ActiveAlert[];
}

export function ActiveAlertsPanel({
  alerts,
}: ActiveAlertsPanelProps): React.ReactElement {
  const { t } = useTranslation();

  if (alerts.length === 0) {
    return (
      <div
        className="observability-active-alerts observability-active-alerts--empty"
        data-testid="observability-active-alerts-empty"
        data-state="awaiting_fr_obs_4"
        aria-labelledby="observability-active-alerts-title"
      >
        <h2
          id="observability-active-alerts-title"
          className="observability-active-alerts-title"
        >
          {t("observability.dashboard.alerts.title", "Active alerts")}
        </h2>
        <p
          className="observability-active-alerts-empty-message"
          role="status"
          aria-live="polite"
        >
          {t(
            "observability.dashboard.alerts.empty",
            "Active alerts will appear here once FR-OBS-4 ships.",
          )}
        </p>
      </div>
    );
  }

  return (
    <div
      className="observability-active-alerts"
      data-testid="observability-active-alerts"
      aria-labelledby="observability-active-alerts-title"
    >
      <h2
        id="observability-active-alerts-title"
        className="observability-active-alerts-title"
      >
        {t("observability.dashboard.alerts.title", "Active alerts")}
      </h2>
      <ul
        className="observability-active-alerts-list"
        data-testid="observability-active-alerts-list"
      >
        {alerts.map((a) => (
          <li
            key={a.alert_id}
            className={`observability-active-alerts-row severity-${a.severity}`}
            data-testid="observability-active-alerts-row"
            data-alert-id={a.alert_id}
          >
            <span className="observability-active-alerts-row-name">
              {a.rule_name}
            </span>
            <span className="observability-active-alerts-row-severity">
              {a.severity}
            </span>
            <span className="observability-active-alerts-row-summary">
              {a.summary}
            </span>
            <span className="observability-active-alerts-row-since">
              {a.firing_since}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
